import { ChangeDetectionStrategy, Component, computed, effect, EventEmitter, inject, input, Output, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslocoModule } from '@jsverse/transloco';
import { produce } from 'immer';
import { InputDirective } from 'libs/shared/ui-common/src/lib/components/input';
import { take } from 'rxjs';
import { RuleService } from 'shared/domain';

import {
  FindingType,
  FlowchartElementType,
  FlowchartLinkDto,
  ObservationPermittedDataType,
  RuleType,
  TimeSpanDto,
} from '@treetop/shared/model';

import { LinkValueComponent } from './components/link-value/link-value.component';
import { RecurrenceComponent } from '../../sub-components/recurrence/recurrence.component';
import { FlowchartSidebarWrapperService } from '../flowchart-sidebar-wrapper.service';

@Component({
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    TranslocoModule,
    MatOptionModule,
    MatSelectModule,
    MatButtonModule,
    MatDividerModule,
    MatTabsModule,
    RecurrenceComponent,
    LinkValueComponent,
    MatProgressSpinnerModule,
    InputDirective,
  ],
  selector: 'flowchart-sidebar-link',
  templateUrl: './flowchart-sidebar-link.component.html',
  styleUrls: ['./flowchart-sidebar-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlowchartSidebarLinkComponent {
  protected ruleService = inject(RuleService);
  private flowchartSidebarWrapperService = inject(FlowchartSidebarWrapperService);

  public sidebarElement = input<FlowchartLinkDto | undefined>(undefined);

  @Output() public flowchartElementDataChanged = new EventEmitter<Partial<FlowchartLinkDto>>();

  protected decisionRuleType = signal<RuleType | undefined>(undefined);
  protected decisionODType = signal<ObservationPermittedDataType | undefined>(undefined);
  protected showLinkValue = signal(false);
  protected awaitRuleType = signal(true);
  protected label = computed(() => this.sidebarElement()?.attribute?.value?.textValue ?? '');

  protected sourceDecision = computed(() =>
    this.flowchartSidebarWrapperService.getFlowchartElement(this.sidebarElement().fromFlowchartNodeId),
  );
  protected isSourceOfLinkDecision = computed(() => this.sourceDecision()?.elementType === FlowchartElementType.FlowchartDecisionNode);

  constructor() {
    effect(() => {
      if (this.sourceDecision()) {
        this.setRuleTypes();
      }
    });
  }

  private setRuleTypes() {
    this.awaitRuleType.set(true);

    this.decisionRuleType.set(undefined);
    this.decisionODType.set(undefined);

    if (this.sourceDecision().elementType !== FlowchartElementType.FlowchartDecisionNode) {
      return;
    }

    const FD = this.sourceDecision()?.guidelineCriteria?.findingDefinition;
    const OD = this.sourceDecision()?.guidelineObservation?.observationDefinition;
    const PC = this.sourceDecision()?.guidelineCriteria?.proposedComponent;

    this.showLinkValue.set(!!(this.sourceDecision() || FD || OD || PC));

    if (this.sourceDecision()?.rule) {
      this.setRuleTypeFromRule(this.sourceDecision()?.rule);
    } else {
      if (FD) {
        if (FD.findingType === FindingType.Quantity) {
          this.decisionRuleType.set(RuleType.Number);
        } else {
          this.decisionRuleType.set(RuleType.Boolean);
        }
      } else if (PC) {
        this.decisionRuleType.set(RuleType.String);
      } else if (OD) {
        if (OD?.permittedDataType >= 0) {
          this.decisionODType.set(OD?.permittedDataType);
        } else {
          this.decisionODType.set(ObservationPermittedDataType.Boolean);
        }
      } else {
        this.decisionRuleType.set(null);
      }
    }

    this.awaitRuleType.set(false);
  }

  private setRuleTypeFromRule(rule: string) {
    this.ruleService
      .validateRule(rule, true)
      .pipe(take(1))
      .subscribe((result) => {
        this.decisionRuleType.set(result.ruleType);
        this.awaitRuleType.set(false);
      });
  }

  protected onLinkOffsetOffsetChanged(offset: TimeSpanDto) {
    const updatedLink = produce(this.sidebarElement(), (draft) => {
      draft.linkOffset ??= {};
      draft.linkOffset.offset = offset;
    });

    this.flowchartElementDataChanged.emit(updatedLink);
  }

  protected onLinkOffsetPosition(position: number) {
    const updatedLink = produce(this.sidebarElement(), (draft) => {
      draft.linkOffset ??= {};
      draft.linkOffset.position = position;
    });

    this.flowchartElementDataChanged.emit(updatedLink);
  }

  protected onLabelChange(value: string) {
    const updatedLink = produce(this.sidebarElement(), (draft) => {
      draft.attribute ??= {};
      draft.attribute.value ??= {};
      draft.attribute.value.textValue = value;
    });

    this.flowchartElementDataChanged.emit(updatedLink);
  }
}
